/**
 * Frequency Role - Convert Pillar
 *
 * Conversão entre unidades de frequência usando hub-and-spoke (via hertz).
 *
 * @example
 * import { convertFrequency, toBaseFrequency, fromBaseFrequency } from '@attrx/role-morphic/frequency/convert';
 *
 * convertFrequency('megahertz', 'hertz', 1);  // 1000000
 * toBaseFrequency('megahertz', 2.4);          // 2400000 (hertz)
 * fromBaseFrequency('megahertz', 2400000);    // 2.4
 */

import { FREQUENCY_UNITS, type FrequencyUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unidade base para conversões (hub) */
export const FREQUENCY_BASE: FrequencyUnit = "hertz";

// =============================================================================
// CORE CONVERSION
// =============================================================================

/**
 * Converte valor para a unidade base (hertz)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em hertz
 *
 * @example
 * toBaseFrequency('megahertz', 2.4);  // 2400000
 * toBaseFrequency('rpm', 60);         // 1
 */
export function toBaseFrequency(variant: FrequencyUnit, value: number): number {
  const config = FREQUENCY_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown frequency unit: ${variant}`);
  }
  return value * (config.factor ?? 1);
}

/**
 * Converte valor da unidade base (hertz) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em hertz
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseFrequency('megahertz', 2400000);  // 2.4
 * fromBaseFrequency('rpm', 1);              // 60
 */
export function fromBaseFrequency(variant: FrequencyUnit, baseValue: number): number {
  const config = FREQUENCY_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown frequency unit: ${variant}`);
  }
  return baseValue / (config.factor ?? 1);
}

/**
 * Converte entre duas unidades de frequência
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertFrequency('megahertz', 'hertz', 1);  // 1000000
 * convertFrequency('rpm', 'hertz', 60);       // 1
 */
export function convertFrequency(
  from: FrequencyUnit,
  to: FrequencyUnit,
  value: number,
): number {
  if (from === to) {
    return value;
  }
  const baseValue = toBaseFrequency(from, value);
  return fromBaseFrequency(to, baseValue);
}

/**
 * Versão safe de convertFrequency que retorna Result
 *
 * @example
 * tryConvertFrequency('megahertz', 'hertz', 1);  // { ok: true, value: 1000000 }
 * tryConvertFrequency('invalid', 'hertz', 1);    // { ok: false, error: '...' }
 */
export function tryConvertFrequency(
  from: FrequencyUnit,
  to: FrequencyUnit,
  value: number,
): Result<number> {
  try {
    return { ok: true, value: convertFrequency(from, to, value) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se uma variante existe
 *
 * @example
 * hasFrequencyVariant('hertz');    // true
 * hasFrequencyVariant('invalid');  // false
 */
export function hasFrequencyVariant(variant: string): variant is FrequencyUnit {
  return variant in FREQUENCY_UNITS;
}

/**
 * Retorna lista de todas as variantes disponíveis
 *
 * @example
 * getFrequencyVariants();  // ['terahertz', 'gigahertz', 'megahertz', ...]
 */
export function getFrequencyVariants(): FrequencyUnit[] {
  return Object.keys(FREQUENCY_UNITS) as FrequencyUnit[];
}
